import request from '../utils/app/request';
import STATE from '../constants/state';
import gma from '../services/gma';
import utils from '../utils';
import {CONFIG} from '../appConfig';

/**
 * @private
 * @desc Embed a form in the DOM to support single sign on and cross-sale redirects
 * @param {String} action
 * @param {String} token
 * @param {String} loyaltyType
 */
function setFormAndSubmit(action, token, loyaltyType) {
  const authTokenName = 'authToken',
    loyaltyTypeName = 'loyaltyType';
  let form = document.querySelector('#sso-form'),
    inputToken,
    inputLoyaltyType;
  if (form === null) {
    form = document.createElement('form');
    form.setAttribute('id', 'sso-form');
    form.setAttribute('method', 'POST');

    inputToken = document.createElement('input');
    inputToken.setAttribute('type', 'hidden');
    inputToken.setAttribute('name', authTokenName);

    inputLoyaltyType = document.createElement('input');
    inputLoyaltyType.setAttribute('type', 'hidden');
    inputLoyaltyType.setAttribute('name', loyaltyTypeName);

    form.appendChild(inputToken);
    form.appendChild(inputLoyaltyType);
    document.body.appendChild(form);
  } else {
    inputToken = form.querySelector('input[name="' + authTokenName + '"]');
    inputLoyaltyType = form.querySelector(
      'input[name="' + loyaltyTypeName + '"]'
    );
  }
  inputToken.setAttribute('value', token);
  inputLoyaltyType.setAttribute('value', loyaltyType);

  // The `action` to cross-sell to Enterprise does not include a protocol, but
  // it does for National. So we only prepend it if the protocol is not set.
  const hasProtocol = action.includes('https') || action.includes('http');
  const partnerDeeplink = hasProtocol ? action : `https://${action}`;

  form.setAttribute('action', partnerDeeplink);
  form.submit();
}

/**
 * @private
 * @desc Maps the old eweb domains response to updated format
 * @param {Array} resp
 * @returns {Object}
 */
// TODO: deprecate mapLegacyFormat when https://jira.ehi.com/browse/ONE-475 is complete
function mapLegacyFormat(resp) {
  return resp.reduce((acc, curr) => {
    const {countryCode, locale = '', url} = curr;
    const localeCode = locale.toLowerCase();
    if (!acc[countryCode]) {
      acc[countryCode] = {};
      acc[countryCode][localeCode] = {url, is_default: true};
    } else if (!acc[countryCode][localeCode]) {
      acc[countryCode][localeCode] = {url, is_default: false};
    }
    return acc;
  }, {});
}

/**
 * @private
 * @desc Map the config COUNTRY and LOCALE values to a respective cross-brand domain
 * @param {Object|Array} resp
 * @param {String} country
 * @param {String} locale
 */
function getDomainByCountryAndLocale(resp, country, locale) {
  // TODO: deprecate mapLegacyFormat usage when https://jira.ehi.com/browse/ONE-475 is complete
  const map = Array.isArray(resp) ? mapLegacyFormat(resp) : resp;
  if (Object.keys(map).length !== 0) {
    const locales = map[country] || {};
    const domain = locales[locale] ||
      locales[Object.keys(locales).filter(key => locales[key].is_default)[0]] ||
      locales[Object.keys(locales)[0]] ||
      {};
    if (domain.url) return { url:domain.url };
  }
  throw CONFIG.ERROR_DEFAULT;
}

/**
 * @private
 * @desc Retrieve encrypted credentials if user is authenticated
 * @param {Object} data
 * @param {Boolean} doSso
 * @param {String} [loyaltyType]
 */
function getEncryptedCredentials(data, doSso, loyaltyType) {
  const {url} = data;
  // get encrypted credentials if doSso is enabled and loyaltyType exists aka user is authenticated
  if (url && doSso && loyaltyType !== '') {
    return request(gma.session.token())
      .then(
        // if missing encrypted credentials, or request errors, continue as doSso was false
        resp => (resp.ec ? { token: resp.ec, url } : { url }),
        () => ({ url })
      );
  }
  return data;
}

/**
 * @name crossBrandDeepLink
 * @desc A cross brand deep link action wrapper; takes in brand and query string and redirects via SSO
 * @param {Object} opts {brand, doFormPost, doSso, qs}
 * @returns {Function}
 * @memberof actions
 * @example
 * // i.e. when going cross brand from national to enterprise
 * const brand = 'enterprise'; // the brand you want to deep link to
 * const doFormPost = true; // defaults to true
 * const doSso = true; // single sign-on, defaults to true
 * // include any deep link query string
 * const qs = '?a=b&c=d';
 * // ...OR use the "getReservationDeepLinkQs" util...
 * const qs = utils.getReservationDeepLinkQs(reservation);
 * const country = 'UK'; // optional, defaults to CONFIG.COUNTRY
 * const locale = 'en_GB'; // optional, defaults to CONFIG.LOCALE
 *
 * dispatch(crossBrandDeepLink({brand, country, doFormPost, doSso, locale, qs});
 * // {
 * //   action: 'www.enterprise.com/en/redirect.jcr_content.POST.html',
 * //   loyaltyCode: 'EC'
 * //   token: '1a2s3d4f5g6h' // token is user's double encrypted credentials
 * // }
 * // NOTE: when doFormPost is true, GMI will embed a form in the DOM and POST to respective domain url
 * // NOTE: when user is not logged in, token and loyaltyCode will be omitted from response / post payload
 * // NOTE: when 'doSso' is set to false (i.e. alamo > national), token will be omitted from resp / post payload
 */
export function crossBrandDeepLink(opts) {
  // this action does not dispatch anything to the store but we leverage thunk's getState
  // and we could theoretically use actions.request instead of utils.request if we needed to
  return (dispatch, getState) => {
    const {
      brand = CONFIG.BRAND,
      country = CONFIG.COUNTRY,
      doFormPost = true,
      doSso = true,
      legacy = false,
      locale = CONFIG.LOCALE,
      qs = ''
    } = opts;
    const state = getState(),
      loyaltyCodePath = ['gbo', 'profile', 'profile', 'basic_profile', 'loyalty_data', 'loyalty_code'],
      // TODO: update these based on selector patterns in GMI
      loyaltyType = state.getIn(STATE.SESSION.concat(['gma', 'logged_in']), false)
        ? state.getIn(STATE.SESSION.concat(loyaltyCodePath), '')
        : '';

    return request(gma.countryToDomain({endpoint:{brand}, opts:{legacy}}))
      .then(
        resp => getDomainByCountryAndLocale(resp, country, locale.toLowerCase()),
        // handle network request error
        err => ({messages: utils.getFormattedMessages(err.message)})
      )
      .then(
        // check to add encrypted credentials
        data => getEncryptedCredentials(data, doSso, loyaltyType),
        // handle country to domain mapping error; i.e. missing country or locale
        err => ({messages: utils.getFormattedMessages(err)})
      )
      .then(
        data => {
          if (data.url) {
            const action = data.url + qs,
              token = data.token || '';
            if (doFormPost) setFormAndSubmit(action, token, loyaltyType);
            return {action, token, loyaltyType};
          }
          // data could be error message from countryToDomain request
          // or missing country or bad format in countryToDomain response; i.e. {}
          return data;
        }
      );
  };
}
